import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, request } from './client';
import type {
  Action,
  CaseDetail,
  CaseList,
  CaseStatus,
  EvidencePack,
  Health,
  LogTail,
  PolicyConfig,
  PolicyHistory,
  PolicyUpdate,
  RunHistory,
  Severity,
} from './types';

/**
 * Query hooks, one per documented route.
 *
 * Routes come from the authorisation table in `docs/architecture.md`. Each hook is annotated
 * with the minimum role the server enforces, so the required role is visible at the call site
 * and a page can render the right denial instead of an empty list.
 *
 * Every `useQuery` names its error generic. Left to inference it is `unknown`, which does not
 * satisfy the declared `UseQueryResult<_, ApiError>` and costs callers the narrowing they need
 * to tell a 403 they should explain from a transient failure they should retry.
 */

export const queryKeys = {
  cases: (filters: CaseFilters) => ['cases', filters] as const,
  case: (id: string) => ['case', id] as const,
  evidence: (id: string) => ['evidence', id] as const,
  approvals: () => ['approvals'] as const,
  health: () => ['health'] as const,
  runs: (source: string) => ['health', 'runs', source] as const,
  logs: (component: string, source: string, minutes: number) =>
    ['health', 'logs', component, source, minutes] as const,
  config: () => ['config'] as const,
  policyHistory: () => ['policyHistory'] as const,
};

export interface CaseFilters {
  status?: CaseStatus | 'all';
  severity?: Severity | 'all';
  search?: string;
}

function queryString(filters: CaseFilters): string {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.severity && filters.severity !== 'all') params.set('severity', filters.severity);
  if (filters.search?.trim()) params.set('q', filters.search.trim());
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

/**
 * A 403 must not be retried.
 *
 * The default retry would fire three requests at a route the caller's role will never permit,
 * turning one clear denial into a slow one. Same for 404. Transient failures still retry.
 */
function retry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && !error.isTransient) return false;
  return failureCount < 2;
}

/** `GET /api/cases` — IRA.Viewer */
export function useCases(filters: CaseFilters): UseQueryResult<CaseList, ApiError> {
  return useQuery<CaseList, ApiError>({
    queryKey: queryKeys.cases(filters),
    queryFn: ({ signal }) => request<CaseList>(`/api/cases${queryString(filters)}`, { signal }),
    retry,
    // An operations console left open on a wall display should not go stale.
    refetchInterval: 60_000,
  });
}

/** `GET /api/cases/{id}` — IRA.Viewer */
export function useCase(caseId: string): UseQueryResult<CaseDetail, ApiError> {
  return useQuery<CaseDetail, ApiError>({
    queryKey: queryKeys.case(caseId),
    queryFn: ({ signal }) =>
      request<CaseDetail>(`/api/cases/${encodeURIComponent(caseId)}`, { signal }),
    retry,
    enabled: Boolean(caseId),
  });
}

/**
 * `GET /api/cases/{id}/evidence` — IRA.Analyst
 *
 * Separate from the case query because it is a separate route with a higher role: a viewer
 * sees the case and the verdict but not the evidence behind it. Folding them into one query
 * would deny the whole page to a viewer.
 */
export function useEvidence(
  caseId: string,
  enabled: boolean,
): UseQueryResult<EvidencePack, ApiError> {
  return useQuery<EvidencePack, ApiError>({
    queryKey: queryKeys.evidence(caseId),
    queryFn: ({ signal }) =>
      request<EvidencePack>(`/api/cases/${encodeURIComponent(caseId)}/evidence`, { signal }),
    retry,
    enabled: enabled && Boolean(caseId),
    // Redacted evidence for a closed collection does not change.
    staleTime: 5 * 60_000,
  });
}

/**
 * `GET /api/approvals` — IRA.Viewer to read, IRA.Responder to act.
 *
 * The one route the authorisation table in `docs/architecture.md` does not name; it was
 * implied by GSI1's `APPROVAL#PENDING` partition and assumed by this client. Now served by
 * `api/router.py` at this path, and `infra/tests/test_api_stack.py` fails if the deployed
 * routes and the router's table ever disagree.
 *
 * Readable by a viewer deliberately: seeing that containment is waiting on someone is not the
 * authority to grant it, and a queue only responders can see is a queue that expires.
 */
export function useApprovals(): UseQueryResult<{ actions: Action[] }, ApiError> {
  return useQuery<{ actions: Action[] }, ApiError>({
    queryKey: queryKeys.approvals(),
    queryFn: ({ signal }) => request<{ actions: Action[] }>('/api/approvals', { signal }),
    retry,
    refetchInterval: 30_000,
  });
}

/** `GET /api/health` — IRA.Admin */
export function useHealth(enabled: boolean): UseQueryResult<Health, ApiError> {
  return useQuery<Health, ApiError>({
    queryKey: queryKeys.health(),
    queryFn: ({ signal }) => request<Health>('/api/health', { signal }),
    retry,
    enabled,
    // Faster than it was. The page now shows a countdown per source, and the countdown is
    // computed from `next_poll_at` — so refreshing once a minute against a three-minute
    // cadence would let a timer run to zero and sit there for most of a minute before the
    // next value arrived, which reads as a stuck scheduler.
    refetchInterval: 15_000,
  });
}

export const RUNS_PAGE_SIZE = 20;

/**
 * `GET /api/health/runs` — IRA.Admin. Recent polls per source, newest first.
 *
 * Pass an empty source for every pollable source at once. The history is a week deep — runs
 * expire after that because this is operational telemetry rather than the incident record —
 * which at a poll every three minutes is around 3,300 rows per source, so it pages.
 *
 * Paged rather than windowed: the rows accumulate as you fetch more instead of replacing each
 * other. The question this table answers is "when did this source's behaviour change", and
 * answering it means comparing a row against one further up, which a page that swapped its
 * contents would take away.
 */
export function useRuns(
  enabled: boolean,
  source: string,
  limit = RUNS_PAGE_SIZE,
): UseInfiniteQueryResult<InfiniteData<RunHistory>, ApiError> {
  return useInfiniteQuery<RunHistory, ApiError, InfiniteData<RunHistory>, readonly unknown[], string>({
    queryKey: queryKeys.runs(source),
    queryFn: ({ pageParam, signal }) => {
      const query = new URLSearchParams({ limit: String(limit) });
      if (source) query.set('source', source);
      if (pageParam) query.set('after', pageParam);
      return request<RunHistory>(`/api/health/runs?${query}`, { signal });
    },
    initialPageParam: '',
    // `undefined` and not `''`: React Query reads undefined as "no further pages" and sets
    // `hasNextPage` false, where an empty string is a page param it would happily request.
    getNextPageParam: (last) => last.next || undefined,
    retry,
    enabled,
    // Only while the first page is the whole view. A refetch reloads *every* loaded page, so
    // polling would multiply with each one — and someone who has paged back into history is
    // reading, not monitoring, and would not thank us for reshuffling the table under them.
    refetchInterval: (query) => ((query.state.data?.pages.length ?? 1) > 1 ? false : 15_000),
  });
}

export const LOG_POLL_INTERVAL_MS = 5_000;
/**
 * How often the log console asks for more. Five seconds is not chosen for CloudWatch's
 * benefit — it will serve far faster — but because it is the shortest interval at which the
 * window still reads as a log rather than as a flicker, and the cost is one `FilterLogEvents`
 * call while an administrator has the page open.
 */

/**
 * `GET /api/health/logs` — IRA.Admin. A live tail of this platform's own logs.
 *
 * `live` rather than a boolean `enabled`: pausing has to stop the refetch without discarding
 * what is on screen, and unmounting the query would clear the window every time someone
 * paused to read a line.
 */
export function useLogs(
  enabled: boolean,
  { component, source, minutes, live }: LogFilters,
): UseQueryResult<LogTail, ApiError> {
  const query = new URLSearchParams({ component, minutes: String(minutes) });
  if (source) query.set('source', source);
  return useQuery<LogTail, ApiError>({
    queryKey: queryKeys.logs(component, source, minutes),
    queryFn: ({ signal }) => request<LogTail>(`/api/health/logs?${query}`, { signal }),
    retry,
    enabled,
    refetchInterval: live ? LOG_POLL_INTERVAL_MS : false,
    // Keeps the previous window visible while a filter change is in flight, so switching
    // component does not blank the console for a round trip.
    placeholderData: (previous) => previous,
  });
}

export interface LogFilters {
  component: string;
  source: string;
  minutes: number;
  live: boolean;
}

/**
 * `GET /api/config` — IRA.Admin.
 *
 * The policy actually in force, which is the deployed baseline with any runtime override
 * applied on top — not the contents of `cdk.json`. The two differ whenever someone has used
 * `PUT /api/policy`, and showing the deployed values would misreport what will happen to the
 * next case.
 */
export function useConfig(enabled: boolean): UseQueryResult<PolicyConfig, ApiError> {
  return useQuery<PolicyConfig, ApiError>({
    queryKey: queryKeys.config(),
    queryFn: ({ signal }) => request<PolicyConfig>('/api/config', { signal }),
    retry,
    enabled,
  });
}

/** `GET /api/policy/history` — IRA.Admin. Who changed policy, when, and to what. */
export function usePolicyHistory(enabled: boolean): UseQueryResult<PolicyHistory, ApiError> {
  return useQuery<PolicyHistory, ApiError>({
    queryKey: queryKeys.policyHistory(),
    queryFn: ({ signal }) => request<PolicyHistory>('/api/policy/history', { signal }),
    retry,
    enabled,
  });
}

/**
 * `PUT /api/policy` — IRA.Admin. Changes containment policy without a deploy.
 *
 * The server validates by constructing the configuration the override would produce, so
 * anything a deploy would refuse comes back as a 400 whose message explains why — `auto` on
 * an irreversible action, an automatic mode with nobody protected, a non-positive TTL. Show
 * `error.message` rather than a generic failure: it is written for this dialog.
 *
 * Both the effective config and the history are invalidated, because a change writes a new
 * version and the history view is the audit trail for exactly this mutation.
 */
export function useUpdatePolicy() {
  const client = useQueryClient();
  return useMutation<PolicyUpdate, ApiError, { values: Record<string, unknown>; note?: string }>({
    mutationFn: ({ values, note }) =>
      request<PolicyUpdate>('/api/policy', {
        method: 'PUT',
        body: JSON.stringify({ values, note }),
      }),
    onSuccess: (result) => {
      client.setQueryData(queryKeys.config(), result.config);
      void client.invalidateQueries({ queryKey: queryKeys.policyHistory() });
    },
  });
}

/**
 * Approve or reject a pending action — IRA.Responder.
 *
 * The server decides. `assert_distinct_approver` rejects an approval from the principal that
 * requested the action and from any subject of the case, and it refuses outright when the
 * token carries no UPN to check. The UI hides the button in the cases it can see, but a
 * rejection arriving here is the expected path, not an error to paper over — so the message
 * is surfaced verbatim.
 */
export function useDecideAction(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<Action, ApiError, { actionId: string; decision: 'approve' | 'reject'; note?: string }>(
    {
      mutationFn: ({ actionId, decision, note }) =>
        request<Action>(`/api/actions/${encodeURIComponent(actionId)}/${decision}`, {
          method: 'POST',
          body: note ? { note } : {},
        }),
      onSuccess: () => {
        // Approving changes the case status and the approvals queue, not just the action.
        void queryClient.invalidateQueries({ queryKey: queryKeys.approvals() });
        if (caseId) void queryClient.invalidateQueries({ queryKey: queryKeys.case(caseId) });
        void queryClient.invalidateQueries({ queryKey: ['cases'] });
      },
    },
  );
}

/** `POST /api/cases/{id}/enrich` — IRA.Analyst */
export function useEnrich(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      request<void>(`/api/cases/${encodeURIComponent(caseId)}/enrich`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.case(caseId) });
    },
  });
}

/** `POST /api/cases/{id}/analyse` — IRA.Analyst */
export function useAnalyse(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      request<void>(`/api/cases/${encodeURIComponent(caseId)}/analyse`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.case(caseId) });
    },
  });
}
